(ns priorities.views
  (:require [reagent.core :as r]
            [very.re-frame.core :as rf]
            ["react-beautiful-dnd" :refer [DragDropContext Draggable Droppable]]
            [css.core :refer [defstyled get-theme]]
            [css.icons :as icon]
            [css.typography :as typo]
            [css.form :as form]
            [css.vars :as vars]
            [cljss.core :as cljss]
            [main.common :as common]
            [priorities.goals :as goals]
            [priorities.events :refer [*max-goals-per-priority*]]
            [clojure.string :as str]
            [util.core :refer [long-str to-local-time to-iso]]))

(defstyled form-title-s :h1
  {:text-align "center"
   :color vars/title
   :font-size "2.27rem"
   :font-weight 300})

(defstyled form-info-s :p
  {:max-width "80%"
   :text-align "center"
   :color vars/gray
   :margin "3rem 0 5rem"
   :line-height "1.5rem"})

(defstyled form-s :form
  {:max-width "61rem"
   :display "flex"
   :margin "auto"
   :justify-content "center"
   :flex-direction "column"
   :align-items "center"})

(defstyled priorities-wrapper-s :div
  {:width "100%"})

(defstyled select-priorities-s :ul
  {:display "grid"
   :grid-template-columns "1fr 1fr"
   :grid-gap "1.5rem"
   ::cljss/media {[[:max-width "768px"]]
                  {:grid-template-columns "1fr"}}})

(defstyled priority-s :div
  {:padding "1rem"
   :box-shadow "1px 1px 4px rgba(0,0,0,0.2)"
   :font-size "1.25rem"
   :line-height "1.5rem"
   :background-repeat "no-repeat"
   :background-position "1rem center"
   :display "grid"
   :grid-template-columns "auto 1fr max-content"
   :align-items "center"
   :cursor "pointer"
   :transition "all .25s ease-in"
   :&:hover {:box-shadow "1px 1px 5px rgba(0, 0, 0, 0.4)"}})

(defstyled custom-priority-s :div
  {:padding "1rem"
   :box-shadow "1px 1px 4px rgba(0,0,0,0.2)"
   :font-size "1.25rem"
   :line-height "1.5rem"
   :background-repeat "no-repeat"
   :background-position "1rem center"
   :display "grid"
   :grid-template-columns "1fr"
   :align-items "center"})

(defstyled priority-icon-s :i
  {:width "2rem"
   :height "2rem"
   :display "block"
   :background "no-repeat center"
   :margin-right ".5rem"})

(defstyled priority-form-title-s :div
  {:font-size "1.33rem"})

(defstyled custom-buttons-s :div
  {:display "flex"
   :align-items "center"})

(defstyled divider-s :span
  {:border-right "1px solid #eee"
   :height "12px"
   :margin "0 .25rem"})

(defstyled custom-priority-form-s :div
  {:display "grid"
   :grid-template-columns "1fr min-content"
   :grid-gap "30px"
   :align-items "center"})

(defstyled cant-add-priority-s :span
  {:color (get-theme :primary)
   :margin "2rem 0 0"})

(defstyled goals-priorities-wrapper-s :section
  {})

(defstyled goals-priority-s :div
  {:border-bottom "1px solid #afafaf"
   :padding "1.5rem 1rem"
   :display "grid"
   :align-items "center"
   :grid-template-columns "max-content 1fr max-content"
   :&:first-child {:border-top "1px solid #afafaf"}})

(defstyled priorities-list-wrapper-s :div
  {:max-width "840px"
   :width "100%"})

(defstyled drag-icon-s :div
  {:cursor "grab"
   :margin-right "1rem"
   :align-items "center"
   :display "flex"})

(defstyled priority-goals-s :div
  {:grid-column "span 3"})

(defstyled priority-icon-open-s :div
  {:display "flex"
   :cursor "pointer"
   :align-items "center"})

(defstyled priority-icon-closed-s :div
  {:display "flex"
   :transform "rotate(180deg)"
   :cursor "pointer"
   :align-items "center"})

(defn- selectable-priority []
  (let [this (r/current-component)
        {title :title
         icon :icon
         selected? :selected?} (r/props this)]
    (into
     [priority-s {:style {:border (if selected? (str "3px solid " (get-theme :primary)) "3px solid transparent")}}
      [priority-icon-s {:style {:backgroundImage (str "url(assets/icons/" (if icon icon "custom") ".png")}}]
      [:div title]]
     (r/children this))))

(defn- editable-priority [p {:keys [edit delete]}]
  [selectable-priority p
   [custom-buttons-s {:on-click #(.stopPropagation %)}
    [form/naked-button-s {:on-click edit
                          :margin "0 5px"
                          :type "button"} "Edit"]
    [divider-s]
    [form/naked-button-s {:on-click delete
                          :margin "0 5px"
                          :type "button"} "Delete"]]])

(defn- editing-priority []
  (let [input (atom nil)]
    (r/create-class
     {:component-did-mount
      (fn []
        (.focus @input))

      :reagent-render
      (fn [p {:keys [done cancel]}]
        [custom-priority-s {:style {:border "3px solid transparent"}}
         [custom-priority-form-s
          [form/input {:ref #(reset! input %)
                       :max-length 50
                       :required true
                       :font-size "1.25rem"
                       :default-value (:title p)
                       :type "text"}]
          [custom-buttons-s
           [form/naked-button-s {:type "button"
                                 :margin "0 5px"
                                 :on-click (fn [e]
                                             (.preventDefault e)
                                             (.stopPropagation e)
                                             (done p (.-value @input)))} "Add"]
           [divider-s]
           [form/naked-button-s {:on-click #(cancel p)
                                 :margin "0 5px"
                                 :type "button"} "Cancel"]]]])})))

(defn- select-priorities
  "Render a list of default priorities and any custom priorities created by the
  user, along with the option to add/remove custom priorities."
  []
  (let [toggle-priority (fn [p ps] (rf/>evt [:financial-priorities/toggle-priority p ps]))
        add-priority (fn [ps] (rf/>evt [:financial-priorities/add-priority ps]))
        edit-priority (fn [p] (rf/>evt [:financial-priorities/edit-priority p]))
        delete-priority (fn [p] (rf/>evt [:financial-priorities/delete-priority p]))
        done-edit (fn [p title] (rf/>evt [:financial-priorities/confirm-add-priority p title]))
        cancel-add (fn [p] (rf/>evt [:financial-priorities/delete-priority p]))
        cancel-edit (fn [p] (rf/>evt [:financial-priorities/cancel-edit-priority p]))]
    (fn [{:keys [priorities on-next
                 on-cancel can-add-priority?
                 editing? at-least-one-selected?
                 cancel-label]}]
      [form-s {:on-submit #(do (.preventDefault %)
                               (on-next))}
       [common/form-title-s {:data-test "priorities-header"} "Select your top 5 financial priorities."]
       [form-info-s (long-str "Priorities identify where you want to focus your effort in"
                              " financial planning. If you don't see a priority, you can add"
                              " your own. In the next step you will add specific and"
                              " measurable goals under each priority.")]
       [priorities-wrapper-s
        [select-priorities-s
         (for [p priorities]
           (if (:editing? p)
             [:div {:key (:id p)}
              [editing-priority p {:done done-edit
                                   :cancel (if (empty? (:title p))
                                             cancel-add
                                             cancel-edit)}]]

             [:div {:on-click #(toggle-priority p priorities)
                    :key (:id p)
                    :type "button"}
              (if (= (:type p) :default)
                [selectable-priority p]
                [editable-priority p {:delete #(delete-priority p)
                                      :edit #(edit-priority p)}])]))]]
       (if can-add-priority?
         [form/naked-button-s {:on-click #(add-priority priorities)
                               :disabled (or editing? (not can-add-priority?))
                               :type "button"
                               :margin "2rem 0 0"
                               :font-size "16px"}
          [form/naked-button-symbol-s "+"]
          [form/naked-button-text-s "Add my own priority"]]

         [cant-add-priority-s "Please delete a priority before adding a new one"])
       [form/button-row-s
        [form/secondary-button-s {:type "button"
                                  :on-click on-cancel} cancel-label]
        [form/button {:type "submit"
                      :disabled (or editing? (not at-least-one-selected?))
                      :width "9.5rem"} "Next"]]])))

(defn- priority-goals [p add-goal delete-goal]
  (let [can-add? (< (count (:goals p)) *max-goals-per-priority*)]
    [priority-goals-s
     [goals/goals (:goals p) delete-goal]
     [form/naked-button-s {:on-click add-goal
                           :type "button"
                           :disabled (not can-add?)
                           :margin "0 0 0 2rem"}
      [form/naked-button-symbol-s {:size "small"} "+"]
      [form/naked-button-text-s (if can-add?
                                  "Add another goal to this priority"
                                  (str "A priority can have a maximum of " *max-goals-per-priority* " goals"))]]]))

(defn- priorities-list []
  (let [draggable (r/adapt-react-class Draggable)]
    (fn [priorities {:keys [toggle-goals start-adding-goal
                            cancel-add-new-goal
                            delete-goal]}]
      [goals-priorities-wrapper-s
       (for [p priorities]
         [draggable {:key (:id p)
                     :draggable-id (:id p)
                     :index (:order p)}
          (fn [provided snapshot]
            (r/as-element
             [goals-priority-s (merge {:ref (.-innerRef ^js provided)}
                                      (js->clj (.-draggableProps ^js provided)))
              [drag-icon-s (js->clj (.-dragHandleProps ^js provided))
               [icon/menu {:stroke (get-theme :primary) :width "30px" :height "30px"}]]
              [priority-form-title-s (str (inc (:order p)) ". " (:title p))]
              (if (empty? (:goals p))
                [form/naked-button-s {:on-click #(start-adding-goal p)
                                      :type "button"} "Add goal"]
                (if (:showing-goals? p)
                  [priority-icon-open-s [icon/chevron {:on-click #(toggle-goals p)
                                                       :type "button"
                                                       :width "20px"
                                                       :height "20px"}]]
                  [priority-icon-closed-s [icon/chevron {:on-click #(toggle-goals p)
                                                         :type "button"
                                                         :width "20px"
                                                         :height "20px"}]]))
              (when (:showing-goals? p)
                [priority-goals p #(start-adding-goal p) #(delete-goal p %)])]))])])))

(defn ^:export add-goal-modal []
  (let [state (r/atom nil)]
    (fn []
      [goals/add-goal {:add #(rf/>evt [:financial-priorities/add-priority-goal-confirmed %])
                       :cancel #(rf/>evt [:financial-priorities/cancel-add-priority-goal])}])))

(defn- edit-priorities
  "A list of priorities that can be dragged around and reordered, which also
  provide buttons for 'viewing existing goals' and 'adding new goals'. The act
  of adding a new goal will trigger a modal for adding a goal description /
  target date / feeling."
  []
  (let [drag-drop-ctx (r/adapt-react-class DragDropContext)
        droppable (r/adapt-react-class Droppable)
        submitting? (r/atom false)]
    (letfn [(toggle-goals [p] (rf/>evt [:financial-priorities/toggle-goals p]))
            (start-adding-goal [p] (rf/>evt [:financial-priorities/add-priority-goal p]))
            (delete-goal [p g] (rf/>evt [:financial-priorities/delete-priority-goal p g]))
            (reorder-priority [^js info]
                              (rf/>evt [:financial-priorities/reorder-priorities
                                        (.-draggableId info)
                                        (.. info -source -index)
                                        (.. info -destination -index)]))]
      (fn [{:keys [priorities cancel done]}]
        [form-s {:on-submit #(do
                               (reset! submitting? true)
                               (.preventDefault %)
                               (done))}
         [form-title-s "Set specific goals."]
         [form-info-s (long-str "Goals are the specific and measurable ways you actively work"
                                " towards meeting your priorities. You can add multiple goals"
                                " to a priority as well as drag to reorder your priorities.")]
         [drag-drop-ctx {:on-drag-end reorder-priority}
          [droppable {:droppable-id "droppable"}
           (fn [provided snapshot]
             (r/as-element
              [priorities-list-wrapper-s {:ref (.-innerRef ^js provided)}
               [priorities-list priorities {:toggle-goals toggle-goals
                                            :start-adding-goal start-adding-goal
                                            :delete-goal delete-goal}]]))]]
         [form/button-row-s
          [form/secondary-button-s {:on-click cancel
                                    :disabled @submitting?
                                    :type "button"}
           "Select new priorities"]
          [form/button {:type "submit"
                        :disabled @submitting?}
           (if @submitting? "Creating..." "Save")]]]))))

(defn- trim-priorities
  "Remove the statefulness attached to a priority and its goals for form-input
  purposes.

  @TODO we should never have to write this, our server should just ignore fields
  which it doesn't understand."
  [priorities]
  (->> priorities
       (map (fn [x] (select-keys x [:title
                                    :order
                                    :goals])))
       (map (fn [x] (update x :goals
                            (fn [gs] (map (fn [x]
                                            (-> x
                                                (select-keys [:title
                                                              :target-date
                                                              :feeling])
                                                (update :target-date to-iso)))
                                          gs)))))
       (assoc {} :priorities)))

(defn manage-priorities [{:keys [on-save on-cancel on-step-change]
                          :or {on-next #(), on-step-change #()}}]
  (let [form (rf/subscribe [:financial-priorities/form])
        selected (rf/subscribe [:financial-priorities/selected])
        priorities-selected (fn [ps]
                              (on-step-change 1)
                              (rf/>evt [:financial-priorities/priorities-selected ps]))
        select-new-priorities (fn []
                                (on-step-change 0)
                                (rf/>evt [:financial-priorities/change-selected]))
        save-priorities (fn [ps] (on-save (trim-priorities ps)))]
    (r/create-class
      {:display-name "manage-priorities"
       :reagent-render
       (fn [{:keys [cancel-label] :or {cancel-label "Cancel"}}]
         (let [{:keys [priorities action] :as f} @form]
           [common/form-header-s {:grid-column "content"}
            (condp = action
              :selecting [select-priorities {:priorities priorities
                                             :can-add-priority? (:can-add-priority? f)
                                             :editing? (:editing? f)
                                             :at-least-one-selected? (:at-least-one-selected? f)
                                             :on-next #(priorities-selected priorities)
                                             :on-cancel on-cancel
                                             :cancel-label cancel-label}]
              :editing [edit-priorities {:priorities @selected
                                         :cancel select-new-priorities
                                         :done #(save-priorities @selected)}])]))})))

(defstyled priority-wrapper-s :li
  {:border-bottom (str "1px solid " vars/border-light)})

(defstyled priority-header-s :div
  {:display "flex"
   :flex-direction "row"
   :align-items "center"
   :cursor "pointer"
   :width "100%"
   :padding "1.5rem 10px"
   :justify-content "space-between"
   :transition "color .25s ease-in"
   :color vars/gray
   :&:hover {:color (get-theme :primary)}})

(defstyled priority-order-s :div
  {:font-size "1.33rem"
   :margin-right "5px"})

(defstyled priority-title-s :div
  {:font-size "1.33rem"})

(defstyled row-s :div
  {:display "flex"
   :flex-direction "row"
   :align-items "center"})

(defstyled nr-text-s :p
  {:color "#666666"
   :line-height "1.6rem"
   :margin-bottom "1rem"})

(defn- priority [priority {:keys [show-goals? toggle]}]
  [priority-wrapper-s
   [priority-header-s {:on-click toggle
                       :type "button"}
    [row-s
     [priority-order-s (inc (:order priority)) [:span "."]]
     [priority-title-s (:title priority)]]
    (if show-goals?
      [priority-icon-open-s [icon/chevron]]
      [priority-icon-closed-s [icon/chevron]])]
   (when show-goals? [goals/goals (:goals priority)])])

(defn financial-priorities []
  (let [ps (rf/subscribe [:financial-priorities/sorted])
        active-priority (r/atom nil)
        toggle (fn [_ id] (if (= id @active-priority) nil id))]
    (fn [{:keys [on-edit]}]
      [:section
       [common/profile-section-header {:data-test "edit-priorities-button"
                                       :link on-edit
                                       :text "Edit"
                                       :title "My financial priorities"}]
       (if (empty? @ps)
         [:div
          [nr-text-s "What would you like to achieve with your wealth? Identify your top priorities so we can put a plan in place to overcome any obstacles and work toward your greatest goals."]
          [common/complete-button {:text "Enter your financial priorities"
                                   :on-click on-edit}]]
         [:ol
          (doall
           (for [p @ps]
             ^{:key (:id p)}
             [priority p {:show-goals? (= (:id p) @active-priority)
                          :toggle #(swap! active-priority toggle (:id p))}]))])])))
